import { useEffect, useState, type ReactNode } from 'react';
import { Link, NavLink } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { SITE_NAV, FOOTER_MENU } from '../../lib/site';
import { Logo } from './Logo';
import { PillLink } from './Pill';
import { SiteChat } from '../SiteChat';

/**
 * Public website chrome. `overlay` makes the header sit transparently over a hero
 * photograph until the visitor scrolls, exactly as lotusliving.co.uk does.
 */
export function SiteLayout({ children, overlay = false, chat = true }: { children: ReactNode; overlay?: boolean; chat?: boolean }) {
  const [scrolled, setScrolled] = useState(false);
  const [menu, setMenu] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => { window.scrollTo({ top: 0 }); }, []);

  const dark = overlay && !scrolled && !menu;

  return (
    <div className="min-h-screen bg-white font-sans text-ink-800">
      <header
        className={`fixed inset-x-0 top-0 z-50 transition-all duration-500 ${
          dark ? 'border-b border-white/10 bg-transparent py-5' : 'border-b border-ink-200/80 bg-white/95 py-3 backdrop-blur-md'
        }`}
      >
        <div className="site-container flex items-center justify-between gap-6">
          <Logo tone={dark ? 'white' : 'bronze'} className="h-9 sm:h-10" />

          <nav className="hidden items-center gap-8 lg:flex">
            {SITE_NAV.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === '/'}
                className={({ isActive }) =>
                  `text-[13px] font-medium tracking-wide transition ${
                    dark ? (isActive ? 'text-white' : 'text-white/70 hover:text-white') : isActive ? 'text-ink-900' : 'text-ink-500 hover:text-ink-900'
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
          </nav>

          <div className="hidden items-center gap-3 lg:flex">
            <Link to="/login" className={`text-[13px] font-medium transition ${dark ? 'text-white/80 hover:text-white' : 'text-ink-500 hover:text-ink-900'}`}>
              Sign in
            </Link>
            <PillLink to="/rent" variant={dark ? 'white' : 'dark'} small>Find your new home</PillLink>
          </div>

          <button onClick={() => setMenu((v) => !v)} className={`rounded-full p-2 lg:hidden ${dark ? 'text-white' : 'text-ink-900'}`} aria-label="Menu">
            {menu ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>

        {menu && (
          <div className="border-t border-ink-100 bg-white lg:hidden">
            <nav className="site-container flex flex-col py-4">
              {SITE_NAV.map((item) => (
                <NavLink key={item.to} to={item.to} end={item.to === '/'} onClick={() => setMenu(false)} className={({ isActive }) => `display py-3 text-3xl ${isActive ? 'text-bronze-500' : 'text-ink-900'}`}>
                  {item.label}
                </NavLink>
              ))}
              <div className="mt-4 flex flex-col gap-3 border-t border-ink-100 pt-5">
                <PillLink to="/rent">Find your new home</PillLink>
                <Link to="/login" className="text-sm font-medium text-ink-500">Resident or staff? Sign in</Link>
              </div>
            </nav>
          </div>
        )}
      </header>

      <main className={overlay ? '' : 'pt-[72px] sm:pt-[76px]'}>{children}</main>

      <SiteFooter />
      {chat && <SiteChat />}
    </div>
  );
}

export function SiteFooter() {
  return (
    <footer className="relative z-10 bg-ink-900 pb-12 pt-20 text-white lg:pt-28">
      <div className="site-container">
        <div className="max-w-[760px] pb-24 lg:pb-32">
          <h2 className="display text-[40px] sm:text-[52px] lg:text-[64px]"><em>Experience the</em> Lotus Living difference</h2>
          <PillLink to="/rent" variant="bronze" className="mt-10 lg:mt-14">Find your new home</PillLink>
        </div>

        <div className="flex flex-col justify-between gap-14 border-t border-bronze-400/60 pt-12 md:flex-row md:items-end">
          <nav className="grid grid-cols-2 gap-10 sm:grid-cols-3 sm:gap-16">
            {Object.entries(FOOTER_MENU).map(([group, items]) => (
              <div key={group}>
                <div className="mb-4 text-[11px] font-semibold uppercase tracking-[0.2em] text-bronze-400">{group}</div>
                <ul className="space-y-2.5">
                  {items.map((it) => (
                    <li key={it.label}>
                      {'external' in it && it.external
                        ? <a href={it.to} target="_blank" rel="noreferrer" className="text-sm text-white/70 transition hover:text-white">{it.label}</a>
                        : <Link to={it.to} className="text-sm text-white/70 transition hover:text-white">{it.label}</Link>}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </nav>

          <div className="flex flex-col gap-8 md:items-end">
            <div className="flex items-center gap-6 opacity-90">
              {[['ombudsman', 'The Property Ombudsman'], ['nrla', 'National Residential Landlords Association'], ['tds', 'Tenancy Deposit Scheme']].map(([k, alt]) => (
                <img key={k} src={`/images/badges/${k}.png`} alt={alt} className="h-12 w-auto rounded bg-white/95 p-1.5" loading="lazy" />
              ))}
            </div>
            <Logo tone="white" className="h-10" />
          </div>
        </div>
        <div className="mt-14 flex flex-wrap items-center justify-between gap-3 pr-44 text-[11px] text-white/40">
          <span>© {new Date().getFullYear()} Lotus Living. Greater London · Dubai South.</span>
          <span>Platform demo · every price above is live</span>
        </div>
      </div>
    </footer>
  );
}
